/**
 * @unscoped — Frontend error telemetry — Cycle 46.
 *
 * reportError(error, context) — runtime error 를 backend telemetry 로 best-effort 전송.
 * cycle 45 ErrorBoundary 의 console.error 만 의존하던 운영 가시성 갭 봉합.
 *
 * 정책:
 *   - DEV (import.meta.env.DEV) → console.error 만 (네트워크 noise 회피).
 *   - PROD → fetch POST /api/v1/internal/telemetry/error (keepalive=true, 빈 페이지에서도 발행).
 *   - silent fail — telemetry 자체 실패가 사용자 흐름 방해 X (apiClient 의 trace-mismatch
 *     batch flush 와 동일 best-effort 정책).
 *   - rate limit — in-memory token bucket (default 10/min). 동일 페이지에서 error 폭증
 *     (예: render loop) 시 endpoint 폭격 차단.
 *   - 본문 redaction — stack/componentStack 은 길이 제한 (4 KB) + query string 제거
 *     (PII URL 노출 차단).
 *   - sessionId/traceId 자동 부착 — 동일 세션의 다른 backend log 와 correlate (cycle 25 trace-context).
 *
 * 비-목표:
 *   - 재시도 / 큐잉 — 운영 telemetry 손실 허용 (cycle 27 trace-mismatch batch 정책 일관).
 *   - Sentry 등 외부 SaaS 연동 — endpoint 만 표준화, 운영자가 backend 측에서 fanout 결정.
 */

import { getSessionId, newTraceId } from './trace-context';
// Cycle 47 — endpoint URL 상수 SSOT (cycle 46 신규 학습 layer 3).
// frontend 와 backend handler 가 같은 상수 import → URL 변경 시 양쪽 동시 갱신.
import { TELEMETRY_ERROR_ENDPOINT } from '@workchat/shared-types';

const ENDPOINT = TELEMETRY_ERROR_ENDPOINT;
const MAX_STACK_BYTES = 4096;
const RATE_LIMIT_PER_MINUTE = 10;
const RATE_LIMIT_WINDOW_MS = 60_000;

export interface ErrorContext {
  /** React error boundary 의 componentStack — 짧은 component path */
  componentStack?: string;
  /** 발생 위치 (예: 'react-error-boundary', 'window-onerror'). 자유 문자열. */
  source?: string;
  /** 추가 진단 정보 — JSON-serializable shallow object */
  extra?: Record<string, string | number | boolean | null>;
}

// token bucket — 정확한 1분 sliding window 대신 timestamp queue 로 간단 구현.
// queue size = rate limit, 새 요청 시 1분 이전 timestamp pop → 슬랏 확보.
const sentTimestamps: number[] = [];

// Cycle 46 — endpoint health 1회 보고용. backend 가 404 / 5xx 응답 시 한 번만 console.warn
// (반복 호출은 silent 유지 — 사용자 devtools 도배 차단). adversarial review 의 silent loss 봉합.
let warnedBadResponse = false;

function isRateLimited(now: number): boolean {
  while (sentTimestamps.length > 0 && now - sentTimestamps[0]! > RATE_LIMIT_WINDOW_MS) {
    sentTimestamps.shift();
  }
  return sentTimestamps.length >= RATE_LIMIT_PER_MINUTE;
}

function truncateStack(s: string | undefined): string | undefined {
  if (!s) return s;
  // PII 노출 차단 — query string 제거. file:line 정보 보존.
  const stripped = s.replace(/\?[^\s)]+/g, '?…');
  if (stripped.length <= MAX_STACK_BYTES) return stripped;
  return stripped.slice(0, MAX_STACK_BYTES) + '…[truncated]';
}

function safeOrigin(): string {
  try {
    if (typeof window !== 'undefined' && window.location) {
      return `${window.location.pathname}${window.location.search ? '?…' : ''}`;
    }
  } catch { /* ignore */ }
  return '';
}

// test override — null 이면 import.meta.env.DEV 참조, boolean 이면 강제.
let devModeOverride: boolean | null = null;

function isDevEnv(): boolean {
  if (devModeOverride !== null) return devModeOverride;
  try {
    return Boolean((import.meta as { env?: { DEV?: boolean } }).env?.DEV);
  } catch { return false; }
}

/**
 * runtime error 를 backend 로 best-effort 전송.
 *
 * @param error  Error instance — message/stack 추출
 * @param context  컨텍스트 (component stack, 발생 위치 등)
 */
export function reportError(error: Error, context: ErrorContext = {}): void {
  // 항상 console.error — 로컬 devtools 진단 우선. ErrorBoundary 의 cycle 45 동작과 호환.
  // eslint-disable-next-line no-console
  console.error('[telemetry] error', error, context);

  if (isDevEnv()) return;
  if (typeof fetch !== 'function') return;

  const now = Date.now();
  if (isRateLimited(now)) return;
  sentTimestamps.push(now);

  let sessionId = '';
  let traceId = '';
  try {
    sessionId = getSessionId();
    traceId = newTraceId();
  } catch { /* trace-context 자체 실패 — 빈 값으로 진행 */ }

  const body = JSON.stringify({
    message: error.message ?? 'Unknown error',
    name: error.name ?? 'Error',
    stack: truncateStack(error.stack),
    componentStack: truncateStack(context.componentStack),
    source: context.source ?? 'unknown',
    sessionId,
    traceId,
    url: safeOrigin(),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    timestamp: new Date(now).toISOString(),
    extra: context.extra ?? null,
  });

  // keepalive=true — 페이지 종료/네비게이션 도중에도 발행 보장.
  // catch 로 silent fail — 운영 telemetry 손실 허용.
  // Cycle 46 adversarial 봉합: 첫 non-2xx 응답일 때 한 번만 console.warn — endpoint 누락
  // (404/SPA fallback 200 HTML) silent loss 차단. 반복 호출은 noise 회피 위해 silent.
  try {
    void fetch(ENDPOINT, {
      method: 'POST',
      keepalive: true,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-Session-Id': sessionId,
        'X-Trace-Id': traceId,
      },
      body,
    })
      .then((res) => {
        if (!res.ok && !warnedBadResponse) {
          warnedBadResponse = true;
          // eslint-disable-next-line no-console
          console.warn(
            `[telemetry] error endpoint returned status=${res.status} (expected 2xx). ` +
            `endpoint=${ENDPOINT} — backend route 미설정 또는 SPA fallback 가능성. ` +
            `이후 동일 경고는 silent.`,
          );
        }
      })
      .catch(() => { /* network/abort — silent (telemetry 손실 허용) */ });
  } catch {
    /* 동기 throw 도 흡수 (잘못된 endpoint URL 등) */
  }
}

/** test 격리 — production 호출 금지. */
export function _resetTelemetryRateLimitForTests(): void {
  sentTimestamps.length = 0;
  warnedBadResponse = false;
}

/** test 진단 — 현재 윈도우 내 발송 count. */
export function _getTelemetrySentCountForTests(): number {
  return sentTimestamps.length;
}

/** test 격리 — DEV/PROD 강제. null 이면 import.meta.env.DEV 참조 복귀. */
export function _setDevModeForTests(value: boolean | null): void {
  devModeOverride = value;
}

// Cycle 48 — global window error / unhandled promise rejection hook.
//
// ErrorBoundary 는 React tree 의 render error 만 캡처. async (setTimeout, fetch),
// Promise rejection, 외부 script error 등은 React boundary 가 못 잡음. window.onerror /
// window.onunhandledrejection 으로 보완하여 telemetry.reportError() 로 일관 전송.
//
// 중복 등록 방지 — module-level installed flag. 두 번째 호출은 no-op.
let globalHandlersInstalled = false;

/**
 * window.onerror + window.onunhandledrejection 을 reportError 로 연결.
 *
 * main.tsx 에서 React mount 직전 1회 호출 권장. test 환경(jsdom)에서는
 * passive — handler 자체만 등록되고 실제 error 가 발생해야 fire.
 *
 * @returns cleanup 함수 (test 격리용; production 에서는 호출 불필요)
 */
export function setupGlobalErrorHandlers(): () => void {
  if (globalHandlersInstalled) return () => {};
  if (typeof window === 'undefined') return () => {};
  globalHandlersInstalled = true;

  const errorHandler = (event: ErrorEvent): void => {
    const err = event.error instanceof Error
      ? event.error
      : new Error(String(event.message ?? 'window error'));
    reportError(err, {
      source: 'window-onerror',
      extra: {
        filename: event.filename ?? '',
        lineno: event.lineno ?? 0,
        colno: event.colno ?? 0,
      },
    });
  };

  const rejectionHandler = (event: PromiseRejectionEvent): void => {
    const reason = event.reason;
    const err = reason instanceof Error
      ? reason
      : new Error(typeof reason === 'string' ? reason : JSON.stringify(reason));
    reportError(err, { source: 'window-unhandled-rejection' });
  };

  window.addEventListener('error', errorHandler);
  window.addEventListener('unhandledrejection', rejectionHandler);

  return () => {
    window.removeEventListener('error', errorHandler);
    window.removeEventListener('unhandledrejection', rejectionHandler);
    globalHandlersInstalled = false;
  };
}

/** test 격리 — installed flag 리셋 후 다시 setupGlobalErrorHandlers 호출 가능. */
export function _resetGlobalHandlersInstalledForTests(): void {
  globalHandlersInstalled = false;
}
